import { useAutoAnimate } from '@formkit/auto-animate/react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { DocumentDuplicateIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import FormInput from 'components/common/form-input';
import Tooltip from 'components/layout/tooltip';
import { FloorOverUnconditionedSpaceType, SizingItemsType } from 'data/types';
import { useState } from 'react';
import { isNotEmpty } from 'utils/helpers';
import {
  getValidSizingAttributeOptions,
  isValidSizingItem
} from 'utils/sizing';

type FloorOverUnconditionedSpaceProps = {
  floor: FloorOverUnconditionedSpaceType;
  updateFn: (
    floorId: string,
    updates: Partial<FloorOverUnconditionedSpaceType>
  ) => void;
  duplicateFn: (floorId: string) => void;
  deleteFn: (floorId: string) => void;
  updateDefaultSizingFn: (updates: Partial<SizingItemsType>) => void;
};

export default function FloorOverUnconditionedSpaceComponent({
  floor,
  updateFn,
  duplicateFn,
  deleteFn,
  updateDefaultSizingFn
}: FloorOverUnconditionedSpaceProps) {
  const defaultValues = useAppSelector(
    (state) => state.data.defaultSizingItems.floorOverUnconditionedSpace
  );
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [animate] = useAutoAnimate({ duration: 100 });
  const [deleteQueued, setDeleteQueued] = useState(false);
  const sizingItemType = 'floorsOverUnconditionedSpace';
  const isModified = Object.keys(defaultValues)
    .filter((key) => isNotEmpty(defaultValues[key]))
    .some((key) => floor[key] !== defaultValues[key]);

  const handleUpdateWithValidation = (
    changes: Partial<FloorOverUnconditionedSpaceType>
  ) => {
    const newFloor: FloorOverUnconditionedSpaceType = {
      ...floor,
      ...changes
    };
    const isValid = isValidSizingItem(sizingItemType, newFloor);

    updateFn(floor.uniqueId, {
      ...changes,
      valid: isValid
    });
  };

  const toggleAdvancedOptions = () => {
    setShowAdvancedOptions((prev) => !prev);
  };

  const handleDuplicateClick = () => {
    duplicateFn(floor.uniqueId);
  };

  const handleDeleteClick = () => {
    if (!deleteQueued) {
      setDeleteQueued(true);
      return;
    }

    deleteFn(floor.uniqueId);
    setDeleteQueued(false);
  };

  const setAsDefault = () => {
    updateDefaultSizingFn({
      floorOverUnconditionedSpace: {
        ...floor,
        uniqueId: undefined,
        valid: undefined,
        area: undefined,
        partitionUnconditionedType: undefined
      }
    });
  };

  const classes = clsx(
    'flex flex-col gap-3 rounded-xl border-2 bg-gray-100',
    floor.valid ? 'border-gray-200' : 'border-red-500'
  );

  return (
    <div className={classes}>
      <div
        key={floor.uniqueId}
        className='flex w-full flex-col divide-y divide-gray-300 rounded-lg bg-gray-200 px-5'
        ref={animate}
      >
        <div className='flex min-h-[54px] items-center gap-5'>
          <div className='font-semibold'>Floor Over Unconditioned Space</div>
          <div className='w-[130px]'>
            <FormInput
              value={floor.area}
              prefix='Area: '
              placeholder='Area (sq ft)'
              type='number'
              suffix=' sq ft'
              min={1}
              onChange={(value) => handleUpdateWithValidation({ area: value })}
              required
            />
          </div>
          {!floor.valid && (
            <span className='text-red-500'>Invalid Attributes!</span>
          )}
          <div className='ml-auto flex items-center gap-2'>
            <button
              className='relative flex h-full cursor-pointer items-center text-gray-500 hover:text-gray-700'
              type='button'
              onClick={toggleAdvancedOptions}
            >
              <span>Advanced</span>
              {showAdvancedOptions ? (
                <ChevronUpIcon className='h-5 w-5' />
              ) : (
                <ChevronDownIcon className='h-5 w-5' />
              )}
              <Tooltip
                text={
                  showAdvancedOptions
                    ? 'Hide Advanced Options'
                    : 'Show Advanced Options'
                }
              />
            </button>
            <button
              className='relative h-full cursor-pointer text-gray-500 hover:text-gray-700'
              type='button'
              onClick={handleDuplicateClick}
            >
              <DocumentDuplicateIcon className='h-5 w-5' />
              <Tooltip text='Duplicate Floor' />
            </button>
            <button
              className='relative h-full cursor-pointer text-gray-500 hover:text-gray-700'
              type='button'
              onClick={handleDeleteClick}
              onMouseLeave={() => setDeleteQueued(false)}
            >
              <TrashIcon className='h-5 w-5' />
              <Tooltip
                text={deleteQueued ? 'Click Again to Confirm' : 'Delete Floor'}
              />
            </button>
          </div>
        </div>
        {showAdvancedOptions && (
          <div className='flex flex-wrap items-center gap-x-5 gap-y-3 py-3'>
            <FormInput
              value={floor.floorCover}
              label='Floor Cover'
              type='select'
              options={getValidSizingAttributeOptions(
                sizingItemType,
                floor,
                'floorCover'
              )}
              onChange={(value) => {
                handleUpdateWithValidation({
                  floorCover: value
                });
              }}
              required
              preventDeselection
            />
            <FormInput
              value={floor.insulationValue}
              label='Insulation Value'
              type='select'
              options={getValidSizingAttributeOptions(
                sizingItemType,
                floor,
                'insulationValue'
              )}
              onChange={(value) => {
                handleUpdateWithValidation({
                  insulationValue: value as any
                });
              }}
              required
              preventDeselection
            />
            <FormInput
              value={floor.exposedWallInsulationValue}
              label='Exposed Wall Insulation'
              type='select'
              options={getValidSizingAttributeOptions(
                sizingItemType,
                floor,
                'exposedWallInsulationValue'
              )}
              onChange={(value) => {
                handleUpdateWithValidation({
                  exposedWallInsulationValue: value
                });
              }}
              required
              preventDeselection
            />
            <FormInput
              value={floor.unconditionedSpaceInfiltrationType}
              label='Unconditioned Space Infiltration Type'
              type='select'
              options={getValidSizingAttributeOptions(
                sizingItemType,
                floor,
                'unconditionedSpaceInfiltrationType'
              )}
              onChange={(value) => {
                handleUpdateWithValidation({
                  unconditionedSpaceInfiltrationType: value
                });
              }}
              required
              preventDeselection
            />
            <FormInput
              value={floor.partitionUnconditionedType}
              label='Above Garage?'
              type='select'
              options={['Garage with Conditioned Space Above']}
              onChange={(value) => {
                handleUpdateWithValidation({
                  partitionUnconditionedType: value
                });
              }}
            />
            {isModified && (
              <div className='flex w-full justify-end'>
                <button
                  className='relative flex h-full cursor-pointer items-center text-gray-500 hover:text-gray-700'
                  type='button'
                  onClick={setAsDefault}
                >
                  <span>Set as Default</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
